var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _LinkService_externalLinkRel, _LinkService_externalLinkTarget;
/* Copyright 2015 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { EventBus, SimpleLinkService } from 'pdfjs-dist/web/pdf_viewer.mjs';
import invariant from 'tiny-invariant';
const DEFAULT_LINK_REL = 'noopener noreferrer nofollow';
class LinkService extends SimpleLinkService {
    constructor() {
        super({ eventBus: new EventBus() });
        _LinkService_externalLinkRel.set(this, void 0);
        _LinkService_externalLinkTarget.set(this, void 0);
    }
    setDocument(pdfDocument) {
        this.pdfDocument = pdfDocument;
    }
    setViewer(pdfViewer) {
        this.pdfViewer = pdfViewer;
    }
    setExternalLinkRel(externalLinkRel) {
        __classPrivateFieldSet(this, _LinkService_externalLinkRel, externalLinkRel, "f");
    }
    setExternalLinkTarget(externalLinkTarget) {
        __classPrivateFieldSet(this, _LinkService_externalLinkTarget, externalLinkTarget, "f");
    }
    setHash() {
        // Intentionally empty
    }
    setHistory() {
        // Intentionally empty
    }
    get isInPresentationMode() {
        return false;
    }
    get rotation() {
        return 0;
    }
    set rotation(_value) {
        // Intentionally empty
    }
    get pagesCount() {
        return this.pdfDocument ? this.pdfDocument.numPages : 0;
    }
    get page() {
        invariant(this.pdfViewer, 'PDF viewer is not initialized.');
        return this.pdfViewer.currentPageNumber || 0;
    }
    set page(value) {
        invariant(this.pdfViewer, 'PDF viewer is not initialized.');
        this.pdfViewer.currentPageNumber = value;
    }
    addLinkAttributes(link, url, newWindow) {
        link.href = url;
        link.rel = __classPrivateFieldGet(this, _LinkService_externalLinkRel, "f") || DEFAULT_LINK_REL;
        link.target = newWindow ? '_blank' : __classPrivateFieldGet(this, _LinkService_externalLinkTarget, "f") || '';
    }
    goToDestination(dest) {
        return new Promise((resolve) => {
            invariant(this.pdfDocument, 'PDF document not loaded.');
            invariant(dest, 'Destination is not specified.');
            if (typeof dest === 'string') {
                this.pdfDocument.getDestination(dest).then(resolve);
            }
            else if (Array.isArray(dest)) {
                resolve(dest);
            }
            else {
                dest.then(resolve);
            }
        }).then((explicitDest) => {
            invariant(Array.isArray(explicitDest), `"${explicitDest}" is not a valid destination array.`);
            const destRef = explicitDest[0];
            new Promise((resolve) => {
                invariant(this.pdfDocument, 'PDF document not loaded.');
                if (destRef instanceof Object) {
                    this.pdfDocument
                        .getPageIndex(destRef)
                        .then((pageIndex) => {
                        resolve(pageIndex);
                    })
                        .catch(() => {
                        invariant(false, `"${destRef}" is not a valid page reference.`);
                    });
                }
                else if (typeof destRef === 'number') {
                    resolve(destRef);
                }
                else {
                    invariant(false, `"${destRef}" is not a valid destination reference.`);
                }
            }).then((pageIndex) => {
                const pageNumber = pageIndex + 1;
                invariant(this.pdfViewer, 'PDF viewer is not initialized.');
                invariant(pageNumber >= 1 && pageNumber <= this.pagesCount, `"${pageNumber}" is not a valid page number.`);
                this.pdfViewer.scrollPageIntoView({
                    dest: explicitDest,
                    pageIndex,
                    pageNumber,
                });
            });
        });
    }
    goToPage(pageNumber) {
        const pageIndex = pageNumber - 1;
        invariant(this.pdfViewer, 'PDF viewer is not initialized.');
        invariant(pageNumber >= 1 && pageNumber <= this.pagesCount, `"${pageNumber}" is not a valid page number.`);
        this.pdfViewer.scrollPageIntoView({
            pageIndex,
            pageNumber,
        });
    }
    goToXY() {
        // Intentionally empty
    }
    cachePageRef() {
        // Intentionally empty
    }
    getDestinationHash() {
        return '#';
    }
    getAnchorUrl() {
        return '#';
    }
    executeNamedAction() {
        // Intentionally empty
    }
    isPageVisible() {
        return true;
    }
    isPageCached() {
        return true;
    }
    navigateTo(dest) {
        this.goToDestination(dest);
    }
    async executeSetOCGState() {
        // Intentionally empty
    }
}
_LinkService_externalLinkRel = new WeakMap(), _LinkService_externalLinkTarget = new WeakMap();
export default LinkService;
